import type { Editor, JSONContent } from '@tiptap/react';
import { v4 } from 'uuid';
import type { SegmentType } from '@/app/types';

/**
 * 将源数据转换为编辑器内容
 * @param prompts - 时间线数据
 * @returns 编辑器内容结构
 */
export const generateEditorContent = (prompts: SegmentType[]): JSONContent => {
  const content: JSONContent = {
    type: 'doc',
    content: [],
  };

  if (!prompts || prompts.length === 0) return content;

  prompts.forEach((segment) => {
    const paragraphNode: JSONContent = {
      type: 'paragraph',
      content: [],
    };
    // 如果有时间标签，添加时间标签节点
    if (segment.startTime || segment.endTime) {
      paragraphNode.content?.push({
        type: 'timeTag',
        attrs: {
          id: segment.id,
          start: segment.startTime,
          end: segment.endTime,
          color: segment.color,
        },
      });
    }
    if (segment.text) {
      paragraphNode.content?.push({ type: 'text', text: segment.text });
    }
    if (content.content) {
      content.content.push(paragraphNode);
    }
  });

  return content;
};

/**
 * 从编辑器内容中提取结构化数据
 * @param editor - TipTap 编辑器实例
 * @returns 结构化的时间线数据
 */
export const extractContentFromEditor = (editor: Editor | null): SegmentType[] => {
  if (!editor) return [];

  const content: SegmentType[] = [];
  let currentSegment: SegmentType | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'timeTag') {
      const startTime = node.attrs.start || 0;
      const endTime = node.attrs.end || startTime + 1;
      const id = node.attrs.id || v4();
      const color = node.attrs.color;

      currentSegment = {
        id,
        startTime,
        endTime,
        text: '',
        color,
      };

      content.push(currentSegment);
      return false;
    }

    if (node.isText) {
      if (currentSegment) {
        currentSegment.text = (currentSegment.text || '') + node.text;
      } else {
        currentSegment = {
          id: node.attrs.id || v4(),
          startTime: 0,
          endTime: 0,
          text: node.text,
          color: undefined,
        };
        content.push(currentSegment);
      }
    }

    if (node.type.name === 'paragraph' && pos > 0) {
      currentSegment = null;
    }

    return true;
  });

  return content;
};

/**
 * 判断字符是否为 Emoji
 * @param char - 字符串
 * @returns 是否为 Emoji
 */
export const isEmoji = (char: string) => {
  // Emoji Unicode 范围
  const emojiRanges = [
    [0x1f000, 0x1f9ff], // Emoticons
    [0x2600, 0x26ff], // Misc Symbols
    [0x2700, 0x27bf], // Dingbats
    [0xfe00, 0xfe0f], // Variation Selectors
    [0x1f900, 0x1f9ff], // Supplemental Symbols and Pictographs
    [0x1f300, 0x1f5ff], // Misc Symbols & Pictographs
    [0x1f600, 0x1f64f], // Emoticons
    [0x1f680, 0x1f6ff], // Transport & Map Symbols
  ];

  // 检查字符是否在任何 emoji 范围内
  const isEmoji = Array.from(char).some((c) => {
    const code = c.codePointAt(0) || 0;
    return emojiRanges.some(([start, end]) => code >= start && code <= end);
  });

  return isEmoji;
};
